import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';

const EnrollmentTrends = () => {
  const [enrollmentData, setEnrollmentData] = useState({
    class: '',
    boys: '',
    girls: '',
    term: '',
    year: new Date().getFullYear(),
    admissionDate: ''
  });

  const classes = [
    { id: 'nt', name: 'Nursery Top Class', capacity: 40 },
    { id: 'nm', name: 'Nursery Middle Class', capacity: 40 },
    { id: 'p1', name: 'Primary 1', capacity: 45 },
    { id: 'p2', name: 'Primary 2', capacity: 45 },
    { id: 'p3', name: 'Primary 3', capacity: 45 },
    { id: 'p4', name: 'Primary 4', capacity: 45 },
    { id: 'p5', name: 'Primary 5', capacity: 45 },
    { id: 'p6', name: 'Primary 6', capacity: 45 },
    { id: 'p7', name: 'Primary 7', capacity: 45 }
  ];

  // Sample enrollment data
  const classDistribution = [
    { class: 'Nursery Top', students: 35, capacity: 40 },
    { class: 'Nursery Middle', students: 38, capacity: 40 },
    { class: 'Primary 1', students: 42, capacity: 45 },
    { class: 'Primary 2', students: 40, capacity: 45 },
    { class: 'Primary 3', students: 43, capacity: 45 },
    { class: 'Primary 4', students: 41, capacity: 45 },
    { class: 'Primary 5', students: 39, capacity: 45 },
    { class: 'Primary 6', students: 44, capacity: 45 },
    { class: 'Primary 7', students: 38, capacity: 45 }
  ];

  const genderDistribution = [
    { name: 'Boys', value: 180 },
    { name: 'Girls', value: 160 }
  ];

  const COLORS = ['#87CEEB', '#FFEBCD'];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting enrollment data:', enrollmentData);
    // Here you would typically save to your backend
  };

  const totalStudents = classDistribution.reduce((acc, curr) => acc + curr.students, 0);
  const totalCapacity = classDistribution.reduce((acc, curr) => acc + curr.capacity, 0);
  const availableSpaces = totalCapacity - totalStudents;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.dark' }}>
        Enrollment Management
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h5">{totalStudents}</Typography>
                  <Typography color="textSecondary">Total Students</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'secondary.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h5">{availableSpaces}</Typography>
                  <Typography color="textSecondary">Available Spaces</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h5">{Math.round((totalStudents/totalCapacity) * 100)}%</Typography>
                  <Typography color="textSecondary">Capacity Utilization</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enrollment Form */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'secondary.light' }}>
        <Typography variant="h6" gutterBottom>
          New Student Enrollment
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={enrollmentData.class}
                  onChange={(e) => setEnrollmentData({ ...enrollmentData, class: e.target.value })}
                  label="Class"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.name} (Capacity: {cls.capacity})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Admission Date"
                value={enrollmentData.admissionDate}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, admissionDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Boys"
                value={enrollmentData.boys}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, boys: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Girls"
                value={enrollmentData.girls}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, girls: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Term</InputLabel>
                <Select
                  value={enrollmentData.term}
                  onChange={(e) => setEnrollmentData({ ...enrollmentData, term: e.target.value })}
                  label="Term"
                >
                  <MenuItem value="1">Term 1</MenuItem>
                  <MenuItem value="2">Term 2</MenuItem>
                  <MenuItem value="3">Term 3</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
              >
                Submit Enrollment
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Class-wise Distribution
            </Typography>
            <ResponsiveContainer>
              <BarChart data={classDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="class" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="students" fill="#87CEEB" name="Current Students" />
                <Bar dataKey="capacity" fill="#FFEBCD" name="Total Capacity" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Gender Distribution
            </Typography>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={genderDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EnrollmentTrends;
